import type { IPagination } from '@/interfaces';
import { mapToJson, type QueryMap } from '@/utils/objects';
import type { IncomingMessage } from 'http';
import type { NextApiRequest } from 'next';
import type { ParsedUrlQuery } from 'querystring';

export const COOKIE_NAME_JWT = 'NEF_TOKEN';
export const COOKIE_NAME_REFRESH = 'BNEF_REFRESH';
export const APP_AUDIENCE = 'web v1.0.0';

export const COOKIE_NAME_PREVIEW = 'BNEF_PREVIEW_MODE';

export const ensureArray = (x?: any) => {
    if (!x && ![false, 0].includes(x)) {
        // Notably, if x = '', return []
        return [];
    } else if (Array.isArray(x)) {
        return x;
    }
    return [x];
};

export const calculateListStart = (pagination?: IPagination) => {
    if (!pagination) {
        return undefined;
    }

    return pagination.pageSize * (pagination.current - 1) + 1;
};

export const attemptDecodeURIComponent = (x: string) => {
    try {
        return decodeURIComponent(x);
    } catch {
        return x;
    }
};

export const getBlinkForFunctionMnemonic = (mnemonic: string) => {
    return `https://blinks.bloomberg.com/screens/${encodeURIComponent(mnemonic)}`;
};

export const hasValidCookies = (cookies: NextApiRequest['cookies']) => {
    return (
        cookies[COOKIE_NAME_REFRESH] ||
        (cookies[COOKIE_NAME_JWT] &&
            cookies[COOKIE_NAME_JWT].match(/^[A-Za-z0-9_=-]+\.[A-Za-z0-9_=-]+\.[A-Za-z0-9_.+/=-]+$/g)) // Looks like JWT
    );
};

export const isRequestFromSecurityScanner = (req: IncomingMessage) => {
    return (
        req.headers['user-agent']?.toLowerCase().includes('edgescan') ||
        req.headers['user-agent']?.includes('RAPID7') ||
        req.headers['user-agent']?.includes('TVMWASM')
    );
};

export const queryStringAsObj = (search?: string): QueryMap => {
    if (!search) {
        return new Map();
    }

    if (search.indexOf('?') === 0) {
        search = search.substring(1);
    }

    return search.split('&').reduce((result: QueryMap, value: string) => {
        const parts = value.split('=');
        if (parts[0]) {
            try {
                const key = attemptDecodeURIComponent(parts[0]);
                const value = attemptDecodeURIComponent(parts[1]);
                if (key.includes('[')) {
                    const parts = key.match(/^(.+)\[(.*)\]$/);
                    if (parts && parts.length === 3) {
                        const mainKey = parts[1];
                        const subKey = parts[2];
                        if (!result.has(mainKey)) {
                            result.set(mainKey, new Map() as QueryMap);
                        }
                        (result.get(mainKey) as QueryMap).set(subKey, value);
                        return result;
                    }
                }
                if (result.has(key)) {
                    const existing = result.get(key);
                    const arr = ensureArray(existing);
                    arr.push(value);
                    result.set(key, arr);
                } else {
                    result.set(key, value);
                }
            } catch {
                throw new URIError(`URI malformed, parts: ${parts}`);
            }
        }
        return result;
    }, new Map());
};

/**
 * getCorrectHost will use the correct hostname taken from the second argument.
 * @example host = "www.bnef.com"
 * @param endpoint - endpoint to use
 * @param host - host usually taken from `incomingRequest` or window.location
 */
export const getCorrectHost = (endpoint: string, host: string) => {
    return endpoint.replace('{host}', host.replace('www.', ''));
};

export const getLoginRedirectUrl = (returnUrl: string, paramsToPass: QueryMap = new Map()) => {
    let loginSubPath = '';
    // URL object needs protocol and hostname, although we are only parsing the path and search params
    const parsedReturnUrl = new URL(returnUrl, 'https://www.bnef.com');
    // get the redirect url, ensuring we don't lose query parameters when we come back
    const queryParams = queryStringAsObj(parsedReturnUrl.search);
    // pull the "t" parameter out before escaping, as the login can use it directly
    if (queryParams.has('t')) {
        paramsToPass.set('t', queryParams.get('t') as string);
        queryParams.delete('t');
        loginSubPath = '/guest';
    }
    let r = parsedReturnUrl.pathname;

    const query = objAsQueryString(queryParams);
    if (query.length) {
        r = `${r}?${query}`;
    }
    if (r !== '/' && r !== '/core') {
        paramsToPass.set('r', r);
    }
    const paramsStr = objAsQueryString(paramsToPass);
    return '/login' + loginSubPath + (paramsStr ? `?${paramsStr}` : '');
};

export const objAsUrlQuery = (obj: any, path: string = '') => {
    const query = objAsApiQueryString(obj);
    if (query) {
        return path + '?' + query;
    }
    return path;
};

const renderPropertyValue = (property: string, value: string | object | Map<string, any>): string[] => {
    const subProperties: string[] = [];
    if (value instanceof Map) {
        for (const [k, v] of value.entries()) {
            const subProperty = `${property}[${k}]`;
            if (v instanceof Map || (typeof v === 'object' && v !== null && !Array.isArray(v))) {
                subProperties.push(...renderPropertyValue(subProperty, v));
                continue;
            }
            subProperties.push(`${encodeURIComponent(subProperty)}=${encodeURIComponent(v as string)}`);
        }
    } else if (typeof value === 'object' && value !== null) {
        for (const [k, v] of Object.entries(value)) {
            const subProperty = `${property}[${k}]`;
            if (typeof v === 'object') {
                subProperties.push(...renderPropertyValue(subProperty, v));
                continue;
            }
            subProperties.push(`${encodeURIComponent(subProperty)}=${encodeURIComponent(v as string)}`);
        }
    } else {
        subProperties.push(`${encodeURIComponent(property)}=${encodeURIComponent(value as string)}`);
    }
    return subProperties;
};

export const objAsQueryString = (obj: QueryMap | Record<string, any>, exclude: string[] = []) => {
    const str: string[] = [];
    const entries: Iterable<[string, any]> = obj instanceof Map ? obj.entries() : Object.entries(obj);
    for (const [p, val] of entries) {
        if (!exclude.includes(p)) {
            ensureArray(val).forEach((value: string | object) => {
                str.push(...renderPropertyValue(p, value));
            });
        }
    }
    return str.join('&');
};

const objAsApiQuery = (obj: any) => {
    const queryObject: Map<string, any> = new Map();
    for (const p of Object.keys(obj)) {
        if (p !== '_' && obj[p]) {
            let propertyValue = obj[p];
            if (typeof propertyValue === 'string' && propertyValue.includes('|')) {
                propertyValue = propertyValue.split('|');
            }
            let propertyString = p;
            if (propertyString.includes('-')) {
                // replace kebab-case with camelCase for JSON
                propertyString = kebabToCamel(propertyString);
            }
            if (propertyString === 'sort') {
                // replace kebab-case with camelCase for sort values
                if (typeof propertyValue === 'string') {
                    propertyValue = kebabToCamel(propertyValue);
                } else {
                    propertyValue = propertyValue.map((item: string) => kebabToCamel(item));
                }
            }
            queryObject.set(propertyString, propertyValue);
        }
    }
    return queryObject;
};

const kebabToCamel = (propertyString: string) => {
    return propertyString.replace(/-./g, (x: any) => x[1].toUpperCase());
};
export const camelToKebab = (propertyString: string) => {
    return propertyString.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
};

const groupRelatedApiQueryParams = (obj: Map<string, any>) => {
    const queryObject: Map<string, any> = new Map(obj);
    const relatedQueryParams: Record<string, string[]> = {
        pagination: ['page', 'pageSize'],
    };

    Object.keys(relatedQueryParams).forEach((group) => {
        const params = relatedQueryParams[group];
        const grouped: Map<string, any> = new Map();
        params.forEach((param) => {
            if (queryObject.has(param)) {
                grouped.set(param, queryObject.get(param));
                queryObject.delete(param);
            }
        });
        queryObject.set(group, grouped);
    });

    return queryObject;
};

export const objAsApiQueryString = (obj: Map<string, any> | Record<string, any>) => {
    const str: string[] = [];
    const entries: Iterable<[string, any]> = obj instanceof Map ? obj.entries() : Object.entries(obj);
    for (const [p, rawValue] of entries) {
        let propertyValue = rawValue;
        if (typeof propertyValue === 'string' && propertyValue.includes('|')) {
            propertyValue = propertyValue.split('|');
        }
        let propertyString = p;
        if (Array.isArray(propertyValue)) {
            propertyString += '[]';
        }
        ensureArray(propertyValue).forEach((value: string | object) => {
            str.push(...renderPropertyValue(propertyString, value));

            // todo - temporary workaround as I can't figure out how to get symfony to accept kebab-case yet
            if (propertyString.includes('-')) {
                str.push(
                    ...renderPropertyValue(
                        propertyString.replace(/-./g, (x) => x[1].toUpperCase()),
                        value
                    )
                );
            }
        });
    }
    return str.join('&');
};

const flattenObject = (obj: any, prefix = ''): Map<string, any> => {
    const result: Map<string, any> = new Map();
    Object.entries(obj).forEach(([key, value]) => {
        const newKey = prefix ? `${prefix}[${key}]` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            for (const [k, v] of flattenObject(value, newKey)) {
                result.set(k, v);
            }
        } else {
            result.set(newKey, value);
        }
    });
    return result;
};

/**
 * Pre-processing for the search post request JSON body.
 */
export const prepareSearchRequestBody = (obj: any) => {
    return mapToJson(expandNestedApiQueryParams(groupRelatedApiQueryParams(objAsApiQuery(obj))));
};

// Undo prepareSearchRequestBody
export const searchRequestAsFrontendQuery = (obj: any) => {
    const flattened = flattenObject(obj);

    const frontendQuery: Map<string, any> = new Map();
    for (const [key, value] of flattened) {
        frontendQuery.set(camelToKebab(key), Array.isArray(value) ? value.join('|') : value);
    }

    return objAsQueryString(frontendQuery);
};

const createNestedMapWithValue = function (base: Map<string, any>, names: string[], value: any) {
    const firstName = names[0];

    // If this is the last name, set the value directly
    if (names.length === 1) {
        base.set(firstName, value);
        return;
    }

    // Otherwise, ensure the nested Map exists
    if (!base.has(firstName)) {
        base.set(firstName, new Map());
    } else if (!(base.get(firstName) instanceof Map)) {
        // Handle case where the property exists but isn't a Map
        // Store the original value with numeric key
        const prev = base.get(firstName);
        const newMap = new Map();
        newMap.set('0', prev);
        base.set(firstName, newMap);
    }

    // Recursively build the nested structure
    createNestedMapWithValue(base.get(firstName), names.slice(1), value);
};

/**
 * Expand the query object to create proper nested objects for
 * keys that indicate nested parameters.
 *
 *  E.g. { "filter[year][gte]": 6, "pageSize": 5} returns Map{"filter" => Map{"year" => Map{"gte" => 5}}, "pageSize" => 5}
 *
 * @param obj {Map} The query Map to expand
 * @return {Map} The expanded query Map with the original nested keys deleted.
 */
export const expandNestedApiQueryParams = (obj: Map<string, any>): Map<string, any> => {
    const queryObject: Map<string, any> = new Map(obj);

    for (const [p, propertyValue] of obj) {
        if (
            propertyValue === '' ||
            (Array.isArray(propertyValue) && propertyValue.filter((q: any) => q !== '').length === 0)
        ) {
            queryObject.delete(p);
            continue;
        }

        if (p.includes('[') || p.includes(']')) {
            const nestedParamNames = p.match(/([^[\]\s]+)/g); // filter[year][gte] -> ["filter", "year", "gte"];
            if (nestedParamNames) {
                createNestedMapWithValue(queryObject, [...nestedParamNames], propertyValue);
                queryObject.delete(p);
            }
        }
    }

    return queryObject;
};

/**
 * Given a URL and a QueryMap of search params, return the URL given with the search
 * params merged into it.
 *
 * E.g. url = http://test.com?a=b, search = Map{c => 'd'} returns http://test.com?a=b&c=d
 *
 * @param url {string} The URL in which to merge the search params
 * @param search {QueryMap} A map of search params to merge into the URL
 * @return {string} The resulting URL
 */
export const buildFromGivenWithSearch = (url: string, search: QueryMap) => {
    const existingParams = extractSearchParams(url);
    // Merge: existing params first, then overrides from search
    const allParams: QueryMap = new Map([...existingParams, ...search]);
    if (url.indexOf('?') !== -1) {
        url = url.substring(0, url.indexOf('?'));
    }

    const searchArray: string[] = [];
    for (const [key, val] of allParams) {
        if (!val) {
            continue;
        }
        ensureArray(val).forEach((value: string) => {
            searchArray.push(`${key}=${value}`);
        });
    }

    if (searchArray.length > 0) {
        url += `?${searchArray.join('&')}`;
    }

    return url;
};

/**
 * Extract the search params for a given URL
 *
 * @param url {string} The URL from which to extract the params
 * @return {QueryMap} A map of the search params contained within the URL
 */
export const extractSearchParams = (url: string): QueryMap => {
    if (url.indexOf('?') !== -1) {
        return queryStringAsObj(url.substring(url.indexOf('?')));
    }
    return new Map();
};

/**
 * Create a URL from the current view.
 */
export const getUrlFromCurrentView = () => {
    return new URL(window.location.href);
};

export const clipboardCopy = async (text: string) => {
    // Requires a secure browsing context (i.e. HTTPS)
    if (navigator.clipboard) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {}
    }
    return false;
};

/**
 * Certain characters like `/` are not allowed in Grafana so these need to be removed
 * @param pathname {string} The pathname from next e.g `/insights/[id]`
 */
export const sanitisePathForGuts = (pathname: string): string => {
    return pathname
        .slice(1)
        .replace('/', '_') // e.g convert "/popular" to "popular", then replace subsequent '/'s (slashes not allowed in grafana)
        .replace(/_?\[|\]/g, '_'); // e.g convert flagships_[flagship] to flagships_flagship_ (query strings are already stripped off from pathname)
};

// Includes functionality of sanitisePathForGuts
export const generalizeUrlForMetric = (url: string, query: ParsedUrlQuery) => {
    url = url.slice(1).split('?')[0];
    if (url === '') {
        return 'index';
    }
    let urlSections = url.split('/').map((x) => decodeURIComponent(x));
    for (const [key, value] of Object.entries(query)) {
        const search = ensureArray(value);
        urlSections = urlSections.map((x) => (search.includes(x) ? key : x));
    }

    return urlSections.join('_').replace(/_?\[|\]/g, '_');
};

export const removeUndefined = (obj: Record<string, any>): Record<string, any> => {
    // If all else fail, return JSON.parse(JSON.stringify(obj));
    if (Array.isArray(obj)) {
        return obj.map((x) => {
            if (x === undefined) return null;
            if (x === Object(x)) return removeUndefined(x);
            return x;
        });
    }
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => [key, value === Object(value) ? removeUndefined(value) : value])
    );
};

export const correlationHeader = 'correlation-id';

export const serverHttpClientPassthroughHeaders = [
    'accept-language',
    'ak-edge-location',
    'ak-geo-location',
    'authorization',
    correlationHeader,
    'host',
    'referer', // help debugging
    'true-client-ip',
    'x-ak-edge-auth',
    'x-forwarded-for',
];
